import React, { useState, useEffect, useRef } from "react";
import { IconButton, Popover } from "@mui/material";
import InputTextField from "./inputTextField";
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import moment from "moment";
import { ReactComponent as DatePickerIcon } from './date-picker-icon.svg';

export type dateRangeProps = {
    heading?: string;
    handleChange: (start_date: string | null, end_date: string | null) => void;
    value: { start_date: string | null, end_date: string | null };
    placeholder?: string;
    startAdornment?: React.ReactNode;
    disabled?: boolean;
    hiddenClear?: boolean;
}

const DateRankPicker: React.FC<dateRangeProps> = ({ heading, handleChange, hiddenClear, disabled, value, placeholder, startAdornment }) => {
    const [currentDate, setCurrentDate] = useState<Date>(new Date());
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const datePickerRef = useRef<any>(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const handleOpen = (event: any) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysArray = [];

        for (let i = 0; i < firstDayOfMonth; i++) {
            daysArray.push(<div key={`empty-${i}`}></div>);
        }

        for (let i = 1; i <= daysInMonth; i++) {
            const day = new Date(year, month, i);
            const dayString = day.toLocaleDateString("en-Us");
            let className =
                "flex items-center justify-center cursor-pointer w-[46px] h-[46px] rounded-full text-dark-3 dark:text-dark-6 hover:bg-primary hover:text-white";

            if (value.start_date && dayString === value.start_date) {
                className += ` bg-[#000] text-white dark:text-white rounded-r-none`;
            }
            if (value.end_date && dayString === value.end_date) {
                className += " bg-[#000] text-white dark:text-white rounded-l-none";
            }
            if (
                value.start_date &&
                value.end_date &&
                new Date(day) > new Date(value.start_date) &&
                new Date(day) < new Date(value.end_date)
            ) {
                className += " bg-[#000] text-white rounded-none";
            }

            daysArray.push(
                <div
                    key={i}
                    className={className}
                    data-date={dayString}
                    onClick={() => handleDayClick(dayString)}
                >
                    {i}
                </div>,
            );
        }

        return daysArray;
    };

    const handleDayClick = (selectedDay: any) => {
        if (!value.start_date || (value.start_date && value.end_date)) {
            handleChange(selectedDay, null)
        } else {
            if (new Date(selectedDay) < new Date(value.start_date)) {
                handleChange(selectedDay, value.start_date)
            } else {
                handleChange(value.start_date, selectedDay)
            }
        }
    };

    const updateInput = () => {
        if (value.start_date && value.end_date) {
            return `${moment(value.start_date).add(543, 'year').format("DD/MM/YYYY")} - ${moment(value.end_date).add(543, 'year').format("DD/MM/YYYY")}`;
        } else if (value.start_date) {
            return moment(value.start_date).add(543, 'year').format("DD/MM/YYYY");
        } else {
            return "";
        }
    };

    const toggleDatepicker = (e: any) => {
        setIsOpen(!isOpen);
        setAnchorEl(e.currentTarget);
    };

    // const handleApply = () => {
    //     setIsOpen(false);
    // };
    const handleClear = () => {
        handleChange(null, null);
    }

    const handleCancel = () => {
        handleChange(null, null);
        setIsOpen(false);
    };

    const handleDocumentClick = (e: any) => {
        if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
            setIsOpen(false);
        }
    };


    useEffect(() => {
        if (isOpen) {
            document.addEventListener("click", handleDocumentClick);
        } else {
            document.removeEventListener("click", handleDocumentClick);
        }

        return () => {
            document.removeEventListener("click", handleDocumentClick);
        };
    }, [isOpen]);


    return (
        // <section className="w-full bg-white py-20 lg:py-[120px] dark:bg-dark">
        <div className="w-full">
            <div className="w-full flex flex-wrap">
                <div className="w-full">
                    <div className="h-auto">
                        {heading && (
                            <label className="mb-[10px] block text-base font-medium text-dark">
                                {heading}
                            </label>
                        )}

                        <div className="relative h-auto" ref={datePickerRef}>
                            <div className="relative flex items-center">
                                <span className="w-full">
                                    <InputTextField
                                        startAdornment={startAdornment ? startAdornment : <span className="flex justify-start items-center mr-4"><DatePickerIcon /></span>}
                                        id="datepicker"
                                        type="text"
                                        disabled={disabled}
                                        placeholder={placeholder}
                                        // className="w-full rounded-lg border border-stroke bg-transparent py-2.5 pl-[50px] pr-8 text-dark-2 outline-none transition focus:border-primary dark:border-dark-3 dark:text-dark-6 dark:focus:border-primary"
                                        value={updateInput()}
                                        onClick={disabled ? undefined : handleOpen}
                                    />
                                </span>
                                <div
                                    className={`h-full flex justify-end items-center absolute right-0 cursor-pointer pr-2 text-dark-5`}
                                >
                                    {(value.start_date && value.end_date && !hiddenClear) ? (
                                        <IconButton onClick={handleClear}>
                                            <CloseIcon />
                                        </IconButton>
                                    ) : (
                                        <>
                                            {!disabled && (
                                                <IconButton onClick={toggleDatepicker}>
                                                    <ExpandMoreOutlinedIcon
                                                        sx={{
                                                            fontSize: "1.4rem",
                                                            rotate: isOpen ? "180deg" : "0deg"
                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                        </>
                                    )}

                                </div>
                            </div>

                            <Popover
                                id="datepicker-container"
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "left",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                            >

                                <div
                                    id="datepicker-container"
                                    className="shadow-datepicker z-[9] w-full md:w-[380px] h-auto rounded-xl border border-stroke bg-transparent dark:border-dark-3 dark:bg-dark-2"
                                >
                                    <div className="flex items-center justify-between px-5">
                                        <IconButton
                                            id="prevMonth"
                                            className="rounded-md px-2 py-2 text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
                                            onClick={() =>
                                                setCurrentDate(
                                                    new Date(
                                                        currentDate.setMonth(currentDate.getMonth() - 1),
                                                    ),
                                                )
                                            }
                                        >
                                            <KeyboardArrowLeftIcon />
                                        </IconButton>

                                        <div
                                            id="currentMonth"
                                            className="text-lg font-medium text-dark-3"
                                        >
                                            {currentDate.toLocaleString("th-TH", {
                                                month: "long",
                                            })}{" "}
                                            {moment(currentDate).add(543, 'year').format("YYYY")}
                                        </div>

                                        <IconButton
                                            id="nextMonth"
                                            className="rounded-md px-2 py-2 text-dark hover:bg-gray-2 dark:text-white dark:hover:bg-dark"
                                            onClick={() =>
                                                setCurrentDate(
                                                    new Date(
                                                        currentDate.setMonth(currentDate.getMonth() + 1),
                                                    ),
                                                )
                                            }
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </div>
                                    <div className="mb-4 mt-6 grid grid-cols-7 gap-2 px-5">
                                        {["อา.", "จ", "อ", "พ", "พฤ", "ศ", "ส"].map(
                                            (day) => (
                                                <div
                                                    key={day}
                                                    className="text-center text-sm font-medium text-secondary-color"
                                                >
                                                    {day}
                                                </div>
                                            ),
                                        )}
                                    </div>

                                    <div
                                        id="days-container"
                                        className="mt-2 grid grid-cols-7 gap-y-0.5 px-5"
                                    >
                                        {renderCalendar()}
                                    </div>

                                    <div className="mt-5 flex justify-end space-x-2.5 border-t border-stroke p-5 dark:border-dark-3">
                                        {!hiddenClear && (
                                            <button
                                                id="cancelButton"
                                                className="rounded-lg border border-primary px-5 py-2.5 text-base font-medium text-primary hover:bg-blue-light-5"
                                                onClick={handleCancel}
                                            >
                                                ล้างค่า
                                            </button>
                                        )}
                                        {/* <button
                                            id="applyButton"
                                            className="rounded-lg bg-primary px-5 py-2.5 text-base font-medium text-white hover:bg-[#1B44C8]"
                                            onClick={handleClose}
                                        >
                                            ยืนยัน
                                        </button> */}
                                    </div>
                                </div>


                            </Popover>

                        </div>
                    </div>
                </div>
            </div>
        </div >
        // </section>
    );
}


export default DateRankPicker